import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { deleteContactsByTag } from '../../api/contacts';
import { useOrganizationStore } from '../../stores/useOrganizationStore';

interface UseDeleteContactsByTagResult {
  deleteContacts: (tag: string) => Promise<any>;
  isLoading: boolean;
  error: Error | null;
  isSuccess: boolean;
  reset: () => void;
}

/**
 * Hook for deleting contacts by tag
 * @returns Object with deleteContacts function, loading state, error state, and success state
 */
export const useDeleteContactsByTag = (): UseDeleteContactsByTagResult => {
  const { organization } = useOrganizationStore();
  const [error, setError] = useState<Error | null>(null);

  const mutation = useMutation({
    mutationFn: async (tag: string) => {
      if (!organization?.id) {
        throw new Error('Organization ID is required');
      }

      try {
        // Call the API and return the full response
        const result = await deleteContactsByTag(organization.id, tag);
        return result;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to delete contacts');
        setError(error);
        throw error;
      }
    },
  });

  const deleteContacts = async (tag: string) => {
    setError(null);
    // Return the full response data from the API
    const response = await mutation.mutateAsync(tag);
    return response;
  };

  return {
    deleteContacts,
    isLoading: mutation.isPending,
    error,
    isSuccess: mutation.isSuccess,
    reset: mutation.reset,
  };
};

export default useDeleteContactsByTag;
